import styled, { css } from 'styled-components';

/**
 * Modern Card components with glassy effects
 * These components provide a consistent card styling across the application
 * with modern glass morphism effects
 */

export const Card = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.18);
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }
  
  ${props => props.interactive && css`
    cursor: pointer;
  `}
  
  ${props => props.flat && css`
    box-shadow: none;
    border: 1px solid rgba(0, 0, 0, 0.08);
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }
  `}
  
  ${props => props.accent && css`
    border-top: 3px solid ${props.theme.colors.primary};
  `}
  
  ${props => props.success && css`
    border-top: 3px solid ${props.theme.colors.success};
  `}
  
  ${props => props.warning && css`
    border-top: 3px solid ${props.theme.colors.warning};
  `}
  
  ${props => props.error && css`
    border-top: 3px solid ${props.theme.colors.error};
  `}
`;

export const CardHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  ${props => props.centered && css`
    justify-content: center;
    text-align: center;
  `}
  
  ${props => props.gradient && css`
    background: linear-gradient(135deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.primaryDark} 100%);
    color: white;
    border-bottom: none;
  `}
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  
  ${props => props.light && css`
    color: white;
  `}
`;

export const CardSubtitle = styled.h4`
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  font-weight: 400;
  color: ${props => props.theme.colors.textSecondary};
  
  ${props => props.light && css`
    color: rgba(255, 255, 255, 0.8);
  `}
`;

export const CardBody = styled.div`
  padding: 1.5rem;
  
  ${props => props.compact && css`
    padding: 1rem;
  `}
  
  ${props => props.noPadding && css`
    padding: 0;
  `}
`;

export const CardFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  ${props => props.centered && css`
    justify-content: center;
  `}
  
  ${props => props.right && css`
    justify-content: flex-end;
  `}
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  
  ${props => props.columns && css`
    grid-template-columns: repeat(${props.columns}, 1fr);
    
    @media (max-width: 1200px) {
      grid-template-columns: repeat(${Math.min(props.columns, 3)}, 1fr);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(${Math.min(props.columns, 2)}, 1fr);
    }
    
    @media (max-width: 576px) {
      grid-template-columns: 1fr;
    }
  `}
`;

export const CardDivider = styled.div`
  height: 1px;
  background-color: rgba(0, 0, 0, 0.05);
  margin: 1rem 0;
`;

export const CardImage = styled.div`
  width: 100%;
  height: ${props => props.height || '200px'};
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.5));
    opacity: ${props => props.overlay ? 1 : 0};
    transition: opacity 0.3s ease;
  }
  
  ${Card}:hover & {
    &:after {
      opacity: 1;
    }
  }
`;

export const CardImageContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  color: white;
  z-index: 1;
`;

export const CardBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  margin-right: 0.5rem;
  
  ${props => props.success && css`
    background-color: ${props.theme.colors.success};
  `}
  
  ${props => props.warning && css`
    background-color: ${props.theme.colors.warning};
  `}
  
  ${props => props.error && css`
    background-color: ${props.theme.colors.error};
  `}
  
  ${props => props.outline && css`
    background-color: transparent;
    border: 1px solid ${props.theme.colors.primary};
    color: ${props.theme.colors.primary};
  `}
`;
